import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  deletePaymentMethod,
  getPaymentConfig,
  getPaymentMethods,
  makePaymentMethodDefault,
  payDraftByCard,
  payRevisionByCard,
} from "../../services/paymentMethods";
import { message } from "../../utils/utils";
import AddNewCardForm from "./components/AddNewCardForm";
import PageTitle from "../../components/pageTitle";
import CustomDrawer from "../../components/drawer";
import Loading from "../../components/loading";
import PaymentMethodsItems from "./components/PaymentMethodsItems";
import "./styles.css";

const LEGACY_PAYMENT_METHOD_REATTACH_MESSAGE =
  "This saved card must be re-attached before it can be used for payment.";

const isPayablePaymentMethod = (paymentMethod) => paymentMethod?.provider === "nmi";

const PaymentMethods = ({ pageTitle = "" }) => {
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentConfig, setPaymentConfig] = useState({ provider: "slimcd" });
  const [loading, setLoading] = useState(false);
  const [newCardModalOpen, setNewCardModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState();
  const { draftId, revisionId } = useParams();
  const { state } = useLocation();

  const navigate = useNavigate();

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      getPaymentMethods(),
      getPaymentConfig().catch(() => ({ provider: "slimcd" })),
    ])
      .then(([data, config]) => {
        const methods = Array.isArray(data) ? data : [];
        const defaultPayableMethod = methods.find(
          (item) => item.default && isPayablePaymentMethod(item)
        );
        const firstPayableMethod = methods.find(isPayablePaymentMethod);

        setSelectedItemId(
          (defaultPayableMethod || firstPayableMethod || {}).id
        );
        setPaymentMethods(methods);
        setPaymentConfig(config || { provider: "slimcd" });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDefault = (id) => {
    const paymentMethod = paymentMethods.find((item) => item.id === id);
    if (!isPayablePaymentMethod(paymentMethod)) {
      message({
        message: LEGACY_PAYMENT_METHOD_REATTACH_MESSAGE,
        type: "error",
      });
      return;
    }
    setSelectedItemId(id);
    makePaymentMethodDefault(id).catch(console.error);
  };

  const handleSelectForPaying = (id) => {
    const paymentMethod = paymentMethods.find((item) => item.id === id);
    if (!isPayablePaymentMethod(paymentMethod)) {
      message({
        message: LEGACY_PAYMENT_METHOD_REATTACH_MESSAGE,
        type: "error",
      });
      return;
    }
    setSelectedItemId(id);
  };

  const handleCancel = () => {
    setNewCardModalOpen(false);
  };

  const onPay = () => {
    const id = draftId || revisionId;
    if (!id) {
      return;
    }
    const selectedPaymentMethod = paymentMethods.find(
      (item) => item.id === selectedItemId
    );
    if (!isPayablePaymentMethod(selectedPaymentMethod)) {
      message({
        message: LEGACY_PAYMENT_METHOD_REATTACH_MESSAGE,
        type: "error",
      });
      return;
    }
    const currentDraftId = draftId || (state || {}).draftId;
    const fetchRequest = draftId ? payDraftByCard : payRevisionByCard;
    fetchRequest(id, { paymentMethodId: selectedItemId })
      .then(() => {
        message({
          message: "Your payment has been successfully processed",
          type: "success",
        });
      })
      .catch((error) => {
        console.error({ error });
        message({
          message:
            error?.response?.data?.message ||
            error?.response?.data?.error?.message ||
            "Payment has been rejected",
          type: "error",
        });
      })
      .finally(() =>
        navigate(
          draftId
            ? `/projects/${currentDraftId}/drawing`
            : `/revisions/${revisionId}`,
          {
            state: { refetch: true },
          }
        )
      );
  };

  const onCardDelete = async (id) => {
    const remainingCards = paymentMethods.filter((card) => card.id !== id);
    const nextSelectedCard =
      remainingCards.find((card) => card.default && isPayablePaymentMethod(card)) ||
      remainingCards.find(isPayablePaymentMethod);

    return deletePaymentMethod(id)
      .then(() => {
        setPaymentMethods(remainingCards);
        setSelectedItemId((nextSelectedCard || {}).id);
        message({
          message: "Your credit card has been successfully deleted",
          type: "success",
        });
      })
      .catch((error) => {
        console.error("onCardDelete --- error", error);
        message({ message: "Something went wrong", type: "error" });
      });
  };

  if (loading) {
    return (
      <div className="page-loading-container">
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <PageTitle title={pageTitle} />
      <PaymentMethodsItems
        paymentMethods={paymentMethods}
        handleDefault={handleDefault}
        handleSelectForPaying={handleSelectForPaying}
        onPay={onPay}
        onAddClick={() => setNewCardModalOpen(true)}
        onCardDelete={onCardDelete}
        selectedItemId={selectedItemId}
      />
      <CustomDrawer
        title="Add a Credit Card"
        open={newCardModalOpen}
        onCancel={handleCancel}
        content={
          <AddNewCardForm
            onCancel={handleCancel}
            updateForm={fetchData}
            paymentConfig={paymentConfig}
          />
        }
      />
    </div>
  );
};

export default PaymentMethods;
